/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';

import { cssMap as cssMapCompiled, jsx } from '@compiled/react';
import { FormattedMessage } from 'react-intl-next';

import Checkbox from '@atlaskit/checkbox';
import { Field } from '@atlaskit/form';
import { fg } from '@atlaskit/platform-feature-flags';
import { Text } from '@atlaskit/primitives/compiled';
import { useAnalyticsEvents } from '@atlaskit/teams-app-internal-analytics';

import { TeamMembershipSettings } from '../../types';
import { messages } from '../i18n/messages';

import { CheckboxLine } from './styled';

// TODO: After ptc-people-teams-migrate-styled-components FF cleanup:
// - Remove wrapper pattern (fg() conditionals) and use css={} directly
// - Remove Styled imports and styled.ts references
const compiledStyles = cssMapCompiled({
	checkboxLine: {
		display: 'flex',
		alignItems: 'center',
	},
});

interface TeamTypePickerProps {
	defaultValue?: TeamMembershipSettings;
	onChange: (newValue: TeamMembershipSettings) => void;
}

/**
 * !!It is actually MEMBERSHIP CONTROL, not TEAM TYPE PICKER!!
 */
export const TeamTypePicker = ({
	defaultValue,
	onChange,
}: TeamTypePickerProps): React.JSX.Element => {
	const { fireEvent } = useAnalyticsEvents();

	const handleChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const chosenValue = event.currentTarget.checked
				? TeamMembershipSettings.OPEN
				: TeamMembershipSettings.INVITE_ONLY;

			fireEvent('ui.teamTypePicker.clicked', {
				defaultType: defaultValue || null,
				chosenType: chosenValue,
			});

			onChange(chosenValue);
		},
		[defaultValue, onChange, fireEvent],
	);

	const label = (
		<Text size="small">
			<FormattedMessage {...messages.teamTypeCheckboxLabel} />
		</Text>
	);

	// TODO: Remove wrapper after ptc-people-teams-migrate-styled-components FF cleanup
	const CheckboxLineWrapper = fg('ptc-people-teams-migrate-styled-components')
		? (props: React.HTMLAttributes<HTMLDivElement>) => (
				<div css={compiledStyles.checkboxLine} {...props} />
			)
		: CheckboxLine;

	return (
		<Field name="teamType" label={<FormattedMessage {...messages.teamTypePickerLabel} />}>
			{() => (
				<CheckboxLineWrapper>
					<Checkbox
						{...(fg('team_type_picker_a11y_fix') ? { name: 'teamType' } : {})}
						defaultChecked={defaultValue === TeamMembershipSettings.OPEN}
						onChange={handleChange}
						label={label}
					/>
				</CheckboxLineWrapper>
			)}
		</Field>
	);
};

export default TeamTypePicker;
